import React, { useRef, ReactNode, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { createPortal } from "react-dom";

export type ModalTemplateHandle = {
    close: () => void;
};

type ModalProps = {
    trigger?: ReactNode;
    children: ReactNode;
    className?: string;
    modalId?: string;
    onOpen?: () => void;
};

const ModalTemplate = forwardRef<ModalTemplateHandle, ModalProps>(function ModalTemplate(
    { trigger, children, className = "", modalId, onOpen },
    ref
) {
    const modalRef = useRef<HTMLDialogElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = () => {
        modalRef.current?.showModal();
        setIsOpen(true);
        onOpen?.();
    };

    const handleClose = useCallback(() => {
        modalRef.current?.close();
        setIsOpen(false);
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            close: handleClose,
        }),
        [handleClose]
    );

    const triggerWithClick =
        trigger && React.isValidElement(trigger)
            ? React.cloneElement(trigger as React.ReactElement<any>, {
                  onClick: (e: React.MouseEvent) => {
                      const originalOnClick = (trigger as React.ReactElement<any>).props?.onClick;
                      if (originalOnClick) {
                          originalOnClick(e);
                      }
                      handleOpen();
                  },
              })
            : trigger;

    return (
        <>
            {trigger ? (
                triggerWithClick
            ) : (
                <button type="button" onClick={handleOpen} className="cursor-pointer inline-flex">
                    Open
                </button>
            )}

            {/* Portal the dialog to <body> so it never lands inside a parent <form> (a <dialog> opened
                via showModal() renders in the top layer, so its DOM position has no visual effect). This
                avoids invalid nested-<form> DOM when a modal is used within another form. */}
            {createPortal(
                <dialog ref={modalRef} id={modalId} className="modal" data-testid={modalId} inert={!isOpen || undefined}>
                    <div className={`modal-box ${className}`}>
                        <button
                            type="button"
                            autoFocus={false}
                            className="btn btn-sm text-black btn-ghost absolute right-3 top-4 z-10 text-base"
                            onClick={handleClose}
                        >
                            ✕
                        </button>

                        {children}
                    </div>
                    <div className="modal-backdrop" onClick={handleClose}>
                        <button type="button" aria-label="close">
                            close
                        </button>
                    </div>
                </dialog>,
                document.body
            )}
        </>
    );
});

ModalTemplate.displayName = "ModalTemplate";

export default ModalTemplate;
